<template>
  <!-- Halloween: the face wears the hat the member picked. Off, the face is
       the plain image as before. -->
  <span
    v-if="isHatShown"
    class="relative inline-flex shrink-0"
    :class="square ? 'rounded-xl' : 'rounded-full'"
  >
    <img v-bind="imageAttrs" @error="hasFailed = true" />
    <HalloweenHat :hat="hat" :size="avatarSizes[size]" />
  </span>
  <img v-else v-bind="imageAttrs" @error="hasFailed = true" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { avatarSizes, type AvatarSize } from '@/shared/data/avatarSizes'
import placeholder from '@/assets/images/user_placeholder.svg?url'
import HalloweenHat from '@/shared/components/halloween/HalloweenHat.vue'
import { useHalloween } from '@/shared/composables/useHalloween'

// Fix linter error about single-word component name
defineOptions({
  name: 'AvatarImage',
})

type ShadowSize = '' | 'sm' | 'normal' | 'md' | 'lg' | 'xl' | 'xxl'

type Props = {
  src: string
  size?: AvatarSize
  shadow?: ShadowSize
  disableLazyLoading?: boolean
  square?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  shadow: '',
  disableLazyLoading: false,
  square: false,
})

// A photo that fails to load (an expired LinkedIn URL, a missing file) shows
// the placeholder face, not a broken image.
const hasFailed = ref(false)
watch(
  () => props.src,
  () => (hasFailed.value = false),
)

const shadows: Record<ShadowSize, string> = {
  '': '',
  sm: 'shadow-sm',
  normal: 'shadow',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  xxl: 'shadow-xxl',
}

const imageAttrs = computed(() => ({
  class: [shadows[props.shadow], props.square ? 'rounded-xl' : 'rounded-full'],
  src: hasFailed.value ? placeholder : props.src,
  alt: '',
  test_id: 'avatar',
  loading: props.disableLazyLoading ? ('eager' as const) : ('lazy' as const),
  width: avatarSizes[props.size],
  height: avatarSizes[props.size],
  style: {
    width: `${avatarSizes[props.size]}px`,
    height: `${avatarSizes[props.size]}px`,
  },
}))

// Faces of 32 px and up get the hat; a smaller one is too small to read.
const { enabled: isHalloween, hat } = useHalloween()
const isHatShown = computed(
  () =>
    isHalloween.value && hat.value !== 'none' && avatarSizes[props.size] >= 32,
)
</script>
